/**
 * Finanzas → Pagos de Operadores (`/finanzas/pagos-operadores`).
 *
 * Portado de `Visa_payments` + `views/finance/visa_payments/index.php` y de su
 * bundle `src/finance/visa_payments/index.js`.
 *
 * La pantalla concilia los **cobros con tarjeta** y de las **operadoras de
 * combustible** contra la cuenta del banco: el banco abona el importe con la
 * comisión del operador ya descontada, así que aquí se elige el operador (que fija
 * el porcentaje), se teclea lo que **realmente entró** y se crea el movimiento
 * `VIS_POS`. Cada pago se puede verificar **uno a uno** o **en lote**, marcando los
 * distintos cobros de un mismo depósito.
 *
 * Reglas heredadas:
 * - Se ven las ventas **vivas** cuyo método de pago (primario o secundario) es
 *   `VISA` o `IZIPAY`, cada una como una fila propia.
 * - El rango por defecto es el **mes en curso**, y las tarjetas de cabecera son los
 *   cuatro números del panel anterior: por verificar, verificados, comisiones y
 *   monto neto (todos del filtro entero, no de la página).
 * - El **lote** tiene tope (500 por defecto) y sólo acepta pagos pendientes.
 * - Las filas verificadas llevan el acceso a su **movimiento**; las pendientes, el
 *   botón de verificar y su casilla para el lote.
 * - La **anulación** libera los pagos que componían el movimiento y está reservada a
 *   administradores y managers.
 */

import {
  BadgeCheck,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  FileText,
  Info,
  Layers,
  Percent,
  RefreshCw,
  Search,
  Wallet,
} from 'lucide-react';
import { useMemo, useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CampoFecha } from '@/components/ui/calendario';
import { ConfirmDialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatCurrency, formatDateValue, toIsoDate } from '@/lib/utils';

import {
  DialogoMovimientoPago,
  DialogoVerificarLote,
  DialogoVerificarPago,
} from '../components/pago-operador-dialogos';
import {
  ESTADOS_PAGOS,
  pagosApi,
  useAnularMovimientoPago,
  useMovimientoPago,
  usePagoOperador,
  usePagosOperadores,
  useVerificarLote,
  useVerificarPago,
} from '../pagos-operadores.api';
import type {
  EstadoPagos,
  FiltrosPagos,
  MovimientoPago,
  PagoOperador,
} from '../pagos-operadores.types';

const CONTROL =
  'h-10 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Importe con dos decimales. */
function dinero(valor: number): number {
  return Math.round((Number(valor) || 0) * 100) / 100;
}

export function PagosOperadoresPage() {
  useDocumentTitle('Finanzas · Pagos de Operadores');

  const [desde, setDesde] = useState(() => `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-01`);
  const [hasta, setHasta] = useState(() => toIsoDate(new Date()));
  const [estado, setEstado] = useState<EstadoPagos>('pending');
  const [operador, setOperador] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [pagina, setPagina] = useState(1);
  const [limite, setLimite] = useState(25);
  const [seleccion, setSeleccion] = useState<PagoOperador[]>([]);
  const [paraVerificar, setParaVerificar] = useState<PagoOperador | null>(null);
  const [loteAbierto, setLoteAbierto] = useState(false);
  const [movimientoAbierto, setMovimientoAbierto] = useState<number | null>(null);
  const [porAnular, setPorAnular] = useState<MovimientoPago | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorVerificacion, setErrorVerificacion] = useState<string | null>(null);

  const filtros = useMemo<FiltrosPagos>(
    () => ({
      desde,
      hasta,
      estado,
      operador: operador ? Number(operador) : 0,
      buscar: busqueda.trim(),
      page: pagina,
      perPage: limite,
    }),
    [desde, hasta, estado, operador, busqueda, pagina, limite],
  );

  const consulta = usePagosOperadores(filtros);
  const seleccionado = paraVerificar
    ? `${paraVerificar.tipo_venta}_${paraVerificar.tipo_metodo}`
    : null;
  const venta = usePagoOperador(seleccionado, paraVerificar?.venta_id ?? null);
  const movimiento = useMovimientoPago(movimientoAbierto);

  const verificar = useVerificarPago();
  const verificarLote = useVerificarLote();
  const anular = useAnularMovimientoPago();

  const pagos = consulta.data?.pagos ?? [];
  const totales = consulta.data?.totales;
  const total = consulta.data?.total ?? 0;
  const paginas = consulta.data?.paginas ?? 1;
  const tope = consulta.data?.tope_lote ?? 500;

  const brutoSeleccionado = useMemo(
    () => dinero(seleccion.reduce((suma, pago) => suma + pago.monto, 0)),
    [seleccion],
  );
  const comisionSeleccionada = useMemo(
    () =>
      dinero(
        seleccion.reduce(
          (suma, pago) => suma + (pago.comision ?? 0),
          0,
        ),
      ),
    [seleccion],
  );

  /** Totales de la página, como el pie de la tabla del original. */
  const pieDePagina = useMemo(
    () => ({
      monto: dinero(pagos.reduce((suma, pago) => suma + pago.monto, 0)),
      comision: dinero(pagos.reduce((suma, pago) => suma + (pago.comision ?? 0), 0)),
      neto: dinero(pagos.reduce((suma, pago) => suma + (pago.neto ?? 0), 0)),
    }),
    [pagos],
  );

  /** Cambiar cualquier filtro vuelve a la primera página y limpia la selección. */
  const filtrar = (cambio: () => void) => {
    setPagina(1);
    setSeleccion([]);
    cambio();
  };

  const estaSeleccionado = (pago: PagoOperador) =>
    seleccion.some((item) => item.pago_id === pago.pago_id);

  const alternar = (pago: PagoOperador) => {
    setSeleccion((actual) =>
      actual.some((item) => item.pago_id === pago.pago_id)
        ? actual.filter((item) => item.pago_id !== pago.pago_id)
        : [...actual, pago],
    );
  };

  /** Marca todos los pagos pendientes de la página actual. */
  const seleccionarPagina = () => {
    const pendientesPagina = pagos.filter((pago) => !pago.verificado);

    setSeleccion(pendientesPagina.slice(0, tope));
  };

  /** Pide a la API los pendientes de **todo** el filtro (el «seleccionar todo»). */
  const seleccionarTodo = async () => {
    setError(null);

    try {
      const respuesta = await pagosApi.pendientes(filtros);

      setSeleccion(respuesta.pagos);

      if (respuesta.pagos.length === 0) {
        setError('No hay pagos pendientes con los filtros actuales.');
        return;
      }

      if (respuesta.total > respuesta.incluidos) {
        setAviso(
          `Se seleccionaron los primeros ${respuesta.incluidos} pagos pendientes de ${respuesta.total.toLocaleString(
            'es-PE',
          )}. Verifícalos y vuelve a entrar para continuar con el resto.`,
        );
        setTimeout(() => setAviso(null), 9000);
      }
    } catch (fallo) {
      setError(
        fallo instanceof Error ? fallo.message : 'No se pudieron leer los pagos pendientes.',
      );
    }
  };

  const confirmarVerificacion = (datos: {
    operador_id: string;
    cuenta_id: string;
    metodo_pago_id: string;
    fecha: string;
    referencia: string;
    notas: string;
    comprobante: File | null;
  }) => {
    if (!paraVerificar) return;

    setErrorVerificacion(null);

    verificar.mutate(
      {
        tipo: `${paraVerificar.tipo_venta}_${paraVerificar.tipo_metodo}`,
        venta_id: String(paraVerificar.venta_id),
        ...datos,
      },
      {
        onSuccess: (resultado) => {
          setParaVerificar(null);
          setAviso(
            `Pago verificado: ${paraVerificar.documento ?? ''} por ${formatCurrency(
              paraVerificar.monto,
            )} (comisión ${formatCurrency(resultado.comision)}, neto ${formatCurrency(
              resultado.neto,
            )}, movimiento #${resultado.movement_id}).`,
          );
          setTimeout(() => setAviso(null), 9000);
        },
        onError: (fallo) => {
          setErrorVerificacion(
            fallo instanceof Error ? fallo.message : 'No se pudo verificar el pago.',
          );
        },
      },
    );
  };

  const confirmarLote = (datos: {
    operador_id: string;
    cuenta_id: string;
    metodo_pago_id: string;
    fecha: string;
    monto_real: string;
    referencia: string;
    notas: string;
    comprobante: File | null;
  }) => {
    setErrorVerificacion(null);

    verificarLote.mutate(
      { pagos: seleccion.map((pago) => pago.pago_id), ...datos },
      {
        onSuccess: (resultado) => {
          setLoteAbierto(false);
          setSeleccion([]);
          setAviso(
            `Se verificaron ${resultado.pagos} pago(s) con un movimiento de ${formatCurrency(
              resultado.monto_real,
            )} (diferencia ${formatCurrency(resultado.diferencia)}).`,
          );
          setTimeout(() => setAviso(null), 9000);
        },
        onError: (fallo) => {
          setErrorVerificacion(
            fallo instanceof Error ? fallo.message : 'No se pudo verificar el lote.',
          );
        },
      },
    );
  };

  const confirmarAnulacion = () => {
    if (!porAnular) return;

    setError(null);

    anular.mutate(porAnular.id, {
      onSuccess: (resultado) => {
        setAviso(resultado.message);
        setPorAnular(null);
        setMovimientoAbierto(null);
        setTimeout(() => setAviso(null), 9000);
      },
      onError: (fallo) => {
        setError(fallo instanceof Error ? fallo.message : 'No se pudo anular el movimiento.');
        setPorAnular(null);
      },
    });
  };

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ink-900">Pagos de Operadores</h1>
          <p className="mt-1 text-sm text-ink-500">
            Los cobros con tarjeta y de las operadoras de combustible: aquí se comprueba lo que el
            banco abonó (con la comisión ya descontada) y se crea su movimiento, uno a uno o por
            lotes de depósito.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            leftIcon={
              <RefreshCw
                className={cn('size-4', consulta.isFetching && 'animate-spin')}
                aria-hidden="true"
              />
            }
            onClick={() => void consulta.refetch()}
          >
            Actualizar
          </Button>
        </div>
      </header>

      {aviso ? (
        <Alert variant="success" title="Listo">
          {aviso}
        </Alert>
      ) : null}

      {error ? (
        <Alert variant="error" title="No se pudo completar la acción">
          {error}
        </Alert>
      ) : null}

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
              Desde
              <CampoFecha
                tipo="date"
                value={desde}
                max={hasta}
                onChange={(evento) => filtrar(() => setDesde(evento.target.value))}
                aria-label="Fecha de inicio"
              />
            </label>

            <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
              Hasta
              <CampoFecha
                tipo="date"
                value={hasta}
                min={desde}
                onChange={(evento) => filtrar(() => setHasta(evento.target.value))}
                aria-label="Fecha de fin"
              />
            </label>

            <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
              Estado
              <Select
                value={estado}
                onChange={(evento) => filtrar(() => setEstado(evento.target.value as EstadoPagos))}
                className={CONTROL}
                aria-label="Estado"
              >
                {ESTADOS_PAGOS.map((opcion) => (
                  <option key={opcion.valor} value={opcion.valor}>
                    {opcion.etiqueta}
                  </option>
                ))}
              </Select>
            </label>

            <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
              Operador
              <Select
                value={operador}
                onChange={(evento) => filtrar(() => setOperador(evento.target.value))}
                className={CONTROL}
                aria-label="Operador"
              >
                <option value="">Todos los operadores</option>
                {(consulta.data?.operadores ?? []).map((item) => (
                  <option key={item.id} value={String(item.id)}>
                    {item.etiqueta}
                  </option>
                ))}
              </Select>
            </label>
          </div>

          <div className="mt-3 flex flex-wrap items-end gap-3">
            <label className="flex min-w-[16rem] flex-1 flex-col gap-1 text-xs font-medium text-ink-500">
              Buscar
              <span className="relative block">
                <Search
                  className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-400"
                  aria-hidden="true"
                />
                <Input
                  value={busqueda}
                  onChange={(evento) => filtrar(() => setBusqueda(evento.target.value))}
                  placeholder="Documento, cliente, referencia, operador, monto…"
                  className="pl-9"
                  aria-label="Buscar pago"
                />
              </span>
            </label>

            <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
              Filas
              <Select
                value={String(limite)}
                onChange={(evento) => filtrar(() => setLimite(Number(evento.target.value)))}
                className={cn(CONTROL, 'w-24')}
                aria-label="Filas por página"
              >
                {(consulta.data?.tamanos ?? [10, 25, 50, 100]).map((tamano) => (
                  <option key={tamano} value={String(tamano)}>
                    {tamano}
                  </option>
                ))}
              </Select>
            </label>

            <span className="ml-auto text-xs text-ink-500">
              {total.toLocaleString('es-PE')} pago{total === 1 ? '' : 's'}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Contadores del filtro */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="flex items-center gap-3 p-4">
            <span
              aria-hidden="true"
              className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700"
            >
              <CreditCard className="size-5" />
            </span>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                Por verificar
              </p>
              <p className="text-base font-bold break-words text-amber-800 tabular-nums sm:text-lg">
                {formatCurrency(totales?.monto_pendiente ?? 0)}
              </p>
              <p className="text-xs text-ink-500">
                {(totales?.pendientes ?? 0).toLocaleString('es-PE')} pago(s)
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="border-emerald-200 bg-emerald-50/50">
          <CardContent className="flex items-center gap-3 p-4">
            <span
              aria-hidden="true"
              className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700"
            >
              <BadgeCheck className="size-5" />
            </span>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                Verificados
              </p>
              <p className="text-base font-bold break-words text-emerald-800 tabular-nums sm:text-lg">
                {formatCurrency(totales?.monto_verificado ?? 0)}
              </p>
              <p className="text-xs text-ink-500">
                {(totales?.verificados ?? 0).toLocaleString('es-PE')} pago(s)
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <span
              aria-hidden="true"
              className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-rose-50 text-rose-600"
            >
              <Percent className="size-5" />
            </span>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                Comisiones
              </p>
              <p className="text-base font-bold break-words text-rose-700 tabular-nums sm:text-lg">
                {formatCurrency(totales?.comision ?? 0)}
              </p>
              <p className="text-xs text-ink-500">de lo verificado</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <span
              aria-hidden="true"
              className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-sky-600"
            >
              <Wallet className="size-5" />
            </span>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                Monto neto
              </p>
              <p className="text-base font-bold break-words text-sky-700 tabular-nums sm:text-lg">
                {formatCurrency(totales?.neto ?? 0)}
              </p>
              <p className="text-xs text-ink-500">verificado − comisiones</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Selección masiva */}
      <Card>
        <CardContent className="flex flex-wrap items-center gap-2 p-4">
          <span className="flex items-center gap-2 text-sm font-semibold text-ink-700">
            <Layers className="size-4 text-ink-400" aria-hidden="true" />
            Selección masiva
          </span>

          <Button
            variant="outline"
            size="sm"
            onClick={() => void seleccionarTodo()}
          >
            Seleccionar todo lo filtrado
          </Button>
          <Button variant="outline" size="sm" onClick={seleccionarPagina}>
            Seleccionar la página
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSeleccion([])}
            disabled={seleccion.length === 0}
          >
            Deseleccionar
          </Button>

          {seleccion.length > 0 ? (
            <>
              <Badge variant="brand">
                {seleccion.length} pago(s) · {formatCurrency(brutoSeleccionado)}
                {comisionSeleccionada > 0
                  ? ` · comisión ${formatCurrency(comisionSeleccionada)}`
                  : ''}
              </Badge>

              <Button
                size="sm"
                leftIcon={<CheckCircle2 className="size-3.5" aria-hidden="true" />}
                onClick={() => {
                  setErrorVerificacion(null);
                  setLoteAbierto(true);
                }}
                className="ml-auto"
              >
                Verificar seleccionados ({seleccion.length})
              </Button>
            </>
          ) : (
            <span className="ml-auto text-xs text-ink-500">
              Marca los pagos pendientes para verificarlos con un solo movimiento (máximo {tope}).
            </span>
          )}
        </CardContent>
      </Card>

      {/* Listado */}
      <Card>
        <CardContent className="space-y-3 p-4">
          {consulta.isError ? (
            <Alert variant="error" title="No se pudieron leer los pagos">
              {consulta.error instanceof ApiError
                ? consulta.error.message
                : 'Inténtalo de nuevo en unos segundos.'}
            </Alert>
          ) : null}

          <div className="overflow-auto rounded-card border border-ink-100">
            <table className="w-full border-collapse text-[13px]">
              <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                <tr className="text-left text-xs font-semibold tracking-wide text-ink-500 uppercase">
                  <th className="px-2 py-2">
                    <span className="sr-only">Seleccionar</span>
                  </th>
                  <th className="px-2 py-2">Documento</th>
                  <th className="px-2 py-2">Cliente</th>
                  <th className="px-2 py-2 whitespace-nowrap">Fecha venta</th>
                  <th className="px-2 py-2">Procesador</th>
                  <th className="px-2 py-2">Referencia</th>
                  <th className="px-2 py-2">Operador</th>
                  <th className="px-2 py-2 text-right whitespace-nowrap">Monto</th>
                  <th className="px-2 py-2 text-right whitespace-nowrap">Comisión</th>
                  <th className="px-2 py-2 text-right whitespace-nowrap">Neto</th>
                  <th className="px-2 py-2">Estado</th>
                  <th className="px-2 py-2 text-right whitespace-nowrap">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {consulta.isLoading
                  ? Array.from({ length: 6 }).map((_, fila) => (
                      <tr key={fila} className="border-t border-ink-100">
                        {Array.from({ length: 12 }).map((__, columna) => (
                          <td key={columna} className="px-2 py-3">
                            <Skeleton className="h-4 w-full" />
                          </td>
                        ))}
                      </tr>
                    ))
                  : pagos.map((fila) => (
                      <tr
                        key={fila.pago_id}
                        className={cn('border-t border-ink-100', fila.verificado && 'bg-emerald-50/40')}
                      >
                        <td className="px-2 py-2">
                          {fila.verificado ? (
                            <span className="text-ink-300">—</span>
                          ) : (
                            <input
                              type="checkbox"
                              className="size-4 cursor-pointer accent-brand-500"
                              checked={estaSeleccionado(fila)}
                              onChange={() => alternar(fila)}
                              aria-label={`Seleccionar el pago ${fila.documento ?? fila.pago_id}`}
                            />
                          )}
                        </td>

                        <td className="px-2 py-2">
                          <p className="font-semibold whitespace-nowrap text-ink-800">
                            {fila.documento ?? '—'}
                          </p>
                          <p className="text-[11px] text-ink-500">
                            {fila.tipo_venta_etiqueta} · {fila.tipo_metodo}
                          </p>
                        </td>

                        <td className="max-w-[16rem] px-2 py-2">
                          <p className="truncate" title={fila.cliente}>
                            {fila.cliente}
                          </p>
                          {fila.cliente_documento ? (
                            <p className="truncate text-[11px] text-ink-500">
                              {fila.cliente_documento}
                            </p>
                          ) : null}
                        </td>

                        <td className="px-2 py-2 whitespace-nowrap">
                          <p>{formatDateValue(fila.fecha_venta)}</p>
                          <p className="text-[11px] text-ink-500">
                            {fila.turno ? `turno #${fila.turno}` : fila.hora ?? '—'}
                          </p>
                        </td>

                        <td className="px-2 py-2">
                          <Badge variant={fila.metodo_pago_original === 'IZIPAY' ? 'brand' : 'neutral'}>
                            {fila.metodo_pago_original ?? '—'}
                          </Badge>
                        </td>

                        <td className="max-w-[10rem] px-2 py-2">
                          <span className="block truncate" title={fila.referencia_pago ?? ''}>
                            {fila.referencia_pago || '—'}
                          </span>
                        </td>

                        <td className="px-2 py-2">
                          {fila.operador ? (
                            <>
                              <p className="font-medium whitespace-nowrap text-ink-800">
                                {fila.operador}
                              </p>
                              <p className="text-[11px] text-ink-500">
                                {fila.operador_tasa ?? 0}%
                              </p>
                            </>
                          ) : (
                            <span className="text-ink-300">—</span>
                          )}
                        </td>

                        <td className="px-2 py-2 text-right font-semibold whitespace-nowrap text-ink-900 tabular-nums">
                          {formatCurrency(fila.monto)}
                        </td>

                        <td className="px-2 py-2 text-right whitespace-nowrap text-rose-600 tabular-nums">
                          {fila.comision === null ? '—' : formatCurrency(fila.comision)}
                        </td>

                        <td className="px-2 py-2 text-right whitespace-nowrap text-emerald-700 tabular-nums">
                          {fila.neto === null ? '—' : formatCurrency(fila.neto)}
                        </td>

                        <td className="px-2 py-2">
                          {fila.verificado ? (
                            <Badge variant="success">Verificado</Badge>
                          ) : (
                            <Badge variant="warning">Pendiente</Badge>
                          )}
                        </td>

                        <td className="px-2 py-2">
                          <div className="flex justify-end gap-1">
                            {fila.verificado ? (
                              fila.movement_id ? (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  title={`Ver el movimiento #${fila.movement_id}`}
                                  aria-label={`Ver el movimiento ${fila.movement_id}`}
                                  onClick={() => setMovimientoAbierto(fila.movement_id)}
                                >
                                  <FileText className="size-4" aria-hidden="true" />
                                </Button>
                              ) : (
                                <span className="text-ink-300">—</span>
                              )
                            ) : (
                              <Button
                                size="sm"
                                variant="primary"
                                leftIcon={<CheckCircle2 className="size-3.5" aria-hidden="true" />}
                                onClick={() => {
                                  setErrorVerificacion(null);
                                  setParaVerificar(fila);
                                }}
                              >
                                Verificar
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
              </tbody>
              {!consulta.isLoading && pagos.length > 0 ? (
                <tfoot>
                  <tr className="border-t border-ink-200 bg-ink-50/60 text-[12px] font-semibold text-ink-700">
                    <td className="px-2 py-2" colSpan={7}>
                      Totales de la página ({pagos.length} fila(s))
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums">
                      {formatCurrency(pieDePagina.monto)}
                    </td>
                    <td className="px-2 py-2 text-right text-rose-600 tabular-nums">
                      {formatCurrency(pieDePagina.comision)}
                    </td>
                    <td className="px-2 py-2 text-right text-emerald-700 tabular-nums">
                      {formatCurrency(pieDePagina.neto)}
                    </td>
                    <td className="px-2 py-2" colSpan={2} />
                  </tr>
                </tfoot>
              ) : null}
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-xs text-ink-500">
              Página {pagina} de {paginas.toLocaleString('es-PE')}
              {total > 0
                ? ` · mostrando ${((pagina - 1) * limite + 1).toLocaleString('es-PE')}–${Math.min(
                    pagina * limite,
                    total,
                  ).toLocaleString('es-PE')}`
                : ''}
            </span>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={pagina <= 1}
                leftIcon={<ChevronLeft className="size-4" aria-hidden="true" />}
                onClick={() => setPagina((actual) => Math.max(1, actual - 1))}
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={pagina >= paginas}
                rightIcon={<ChevronRight className="size-4" aria-hidden="true" />}
                onClick={() => setPagina((actual) => actual + 1)}
              >
                Siguiente
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <p className="flex items-start gap-2 text-xs text-ink-500">
        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        La verificación individual registra el movimiento por el <strong>neto</strong> depositado
        (monto menos comisión); la del lote, por el <strong>monto real</strong> que entró al banco, y
        la diferencia contra el neto esperado queda guardada en el detalle. Anular un movimiento
        devuelve sus pagos a la lista de pendientes.
      </p>

      {paraVerificar ? (
        <DialogoVerificarPago
          key={`verificar-${paraVerificar.pago_id}`}
          pago={paraVerificar}
          venta={venta.data}
          cargando={venta.isLoading}
          hoy={consulta.data?.hoy ?? toIsoDate(new Date())}
          guardando={verificar.isPending}
          error={errorVerificacion}
          onCerrar={() => {
            setParaVerificar(null);
            setErrorVerificacion(null);
          }}
          onVerificar={confirmarVerificacion}
        />
      ) : null}

      {loteAbierto ? (
        <DialogoVerificarLote
          abierto={loteAbierto}
          seleccionados={seleccion}
          montoBruto={brutoSeleccionado}
          operadores={consulta.data?.operadores ?? []}
          cuentas={consulta.data?.cuentas ?? []}
          metodos={consulta.data?.metodos_pago ?? []}
          hoy={consulta.data?.hoy ?? toIsoDate(new Date())}
          tope={tope}
          guardando={verificarLote.isPending}
          error={errorVerificacion}
          onCerrar={() => {
            setLoteAbierto(false);
            setErrorVerificacion(null);
          }}
          onVerificar={confirmarLote}
        />
      ) : null}

      <DialogoMovimientoPago
        key={`movimiento-${movimientoAbierto ?? 'cerrado'}`}
        movimientoId={movimientoAbierto}
        consulta={movimiento.data}
        cargando={movimiento.isLoading}
        guardando={anular.isPending}
        error={movimientoAbierto ? error : null}
        onCerrar={() => {
          setMovimientoAbierto(null);
          setError(null);
        }}
        onAnular={(fila) => setPorAnular(fila)}
      />

      <ConfirmDialog
        open={Boolean(porAnular)}
        title="Anular la verificación"
        description={
          porAnular
            ? `Se anulará el movimiento #${porAnular.id} (${formatCurrency(
                porAnular.monto,
              )}) y sus ${porAnular.pagos.length || 1} pago(s) volverán a quedar pendientes de verificar. El comprobante del movimiento se borra.`
            : ''
        }
        confirmLabel="Anular movimiento"
        loading={anular.isPending}
        onConfirm={confirmarAnulacion}
        onClose={() => setPorAnular(null)}
      />
    </div>
  );
}
